import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Info,
  Calculator,
  Banknote,
  FileText,
  Truck,
  Clock,
  ArrowRight,
  Lightbulb,
  Building2
} from 'lucide-react';

const ReglesPage = () => {
  const [activeTab, setActiveTab] = useState('resume');

  return (
    <div className="space-y-6" data-testid="regles-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Les règles du jeu</h1>
        <p className="text-muted-foreground">TVA vs URSSAF : comprendre quand payer quoi</p>
      </div>

      {/* Alerte principale */}
      <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-6 w-6 text-amber-600 mt-0.5" />
            <div>
              <p className="font-bold text-amber-900 text-lg">La règle d'or à retenir</p>
              <p className="text-amber-800 mt-1">
                <strong>URSSAF</strong> = toujours à l'<strong>encaissement</strong> (quand l'argent arrive sur ton compte)<br/>
                <strong>TVA</strong> = ça dépend de ton activité (services vs biens)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="resume">Résumé</TabsTrigger>
          <TabsTrigger value="urssaf">URSSAF</TabsTrigger>
          <TabsTrigger value="tva">TVA</TabsTrigger>
          <TabsTrigger value="pieges">Pièges tréso</TabsTrigger>
        </TabsList>

        {/* RÉSUMÉ */}
        <TabsContent value="resume" className="space-y-6 mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* URSSAF Card */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50 pb-3">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Building2 className="h-5 w-5" />
                  URSSAF (Cotisations sociales)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="p-4 bg-blue-100 rounded-lg text-center mb-4">
                  <p className="text-3xl font-bold text-blue-800">À L'ENCAISSEMENT</p>
                  <p className="text-sm text-blue-700 mt-1">Toujours. Sans exception.</p>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Tu déclares uniquement ce que tu as <strong>réellement encaissé</strong> sur ton compte bancaire.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span>Facture émise mais non payée = <strong>pas déclaré</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span>Acompte reçu = <strong>déclaré immédiatement</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span>Solde payé = <strong>déclaré quand reçu</strong></span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* TVA Card */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-purple-50 pb-3">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Calculator className="h-5 w-5" />
                  TVA (Taxe sur la Valeur Ajoutée)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="p-4 bg-purple-100 rounded-lg text-center mb-4">
                  <p className="text-3xl font-bold text-purple-800">ÇA DÉPEND</p>
                  <p className="text-sm text-purple-700 mt-1">Services vs Biens</p>
                </div>
                <div className="space-y-3">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="font-semibold text-sm flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Prestations de services
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      TVA à l'<strong>encaissement</strong> (sauf option débits)
                    </p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="font-semibold text-sm flex items-center gap-2">
                      <Truck className="h-4 w-4 text-emerald-600" />
                      Vente de biens
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      TVA à la <strong>livraison</strong> (+ acomptes si encaissés)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tableau comparatif */}
          <Card>
            <CardHeader>
              <CardTitle>Tableau comparatif : Quand payer quoi ?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">Situation</th>
                      <th className="text-center p-3 font-semibold">TVA Services</th>
                      <th className="text-center p-3 font-semibold">TVA Biens</th>
                      <th className="text-center p-3 font-semibold">URSSAF</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b hover:bg-muted/50">
                      <td className="p-3">Facture émise, non payée</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <XCircle className="h-3 w-3 mr-1" /> Pas due*
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Due
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <XCircle className="h-3 w-3 mr-1" /> Non déclaré
                        </Badge>
                      </td>
                    </tr>
                    <tr className="border-b hover:bg-muted/50">
                      <td className="p-3">Acompte encaissé</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Due
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Due
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Déclaré
                        </Badge>
                      </td>
                    </tr>
                    <tr className="border-b hover:bg-muted/50">
                      <td className="p-3">Solde payé</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Due
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="secondary">
                          (déjà payée)
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Déclaré
                        </Badge>
                      </td>
                    </tr>
                    <tr className="hover:bg-muted/50">
                      <td className="p-3">Client qui ne paie jamais (impayé)</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <XCircle className="h-3 w-3 mr-1" /> Jamais due
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="h-3 w-3 mr-1" /> Déjà payée 😬
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <XCircle className="h-3 w-3 mr-1" /> Jamais déclaré
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                * Sauf si tu as opté pour la "TVA sur les débits" (dans ce cas, TVA due à la facturation)
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* URSSAF */}
        <TabsContent value="urssaf" className="space-y-6 mt-6">
          <Card className="border-2 border-blue-200">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-blue-900">La règle URSSAF : simple et unique</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center p-6 bg-blue-100 rounded-xl mb-6">
                <p className="text-2xl font-bold text-blue-900">
                  "Je déclare ce que j'ai <span className="underline decoration-4 decoration-blue-500">encaissé</span>, 
                  pas ce que j'ai facturé"
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-bold text-red-800 mb-2 flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Ce qu'on ne déclare PAS
                  </p>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• Factures envoyées mais non payées</li>
                    <li>• Devis acceptés en attente de paiement</li>
                    <li>• Impayés (tant que non récupérés)</li>
                  </ul>
                </div>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Ce qu'on déclare
                  </p>
                  <ul className="text-sm text-emerald-700 space-y-1">
                    <li>• Acomptes reçus</li>
                    <li>• Paiements partiels</li>
                    <li>• Soldes encaissés</li>
                    <li>• Tout ce qui est sur ton compte</li>
                  </ul>
                </div>
              </div>

              <Card className="bg-muted">
                <CardContent className="p-4">
                  <p className="font-semibold mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    Exemple concret
                  </p>
                  <div className="text-sm space-y-2">
                    <p>Tu factures <strong>10 000€ HT</strong> en mars.</p>
                    <p>Le client te paie <strong>3 000€ en mars</strong>, puis <strong>7 000€ en mai</strong>.</p>
                    <div className="flex items-center gap-2 mt-3">
                      <ArrowRight className="h-4 w-4 text-blue-600" />
                      <span>Déclaration URSSAF mars : <strong>3 000€</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-blue-600" />
                      <span>Déclaration URSSAF mai : <strong>7 000€</strong></span>
                    </div>
                    <p className="text-muted-foreground mt-2 italic">
                      L'URSSAF se fiche de la date de facturation. Elle ne voit que ce qui est entré sur ton compte.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TVA */}
        <TabsContent value="tva" className="space-y-6 mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Services */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <FileText className="h-5 w-5" />
                  Prestations de services
                </CardTitle>
                <p className="text-sm text-blue-700">Consultant, formateur, coach, développeur...</p>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="p-4 bg-blue-100 rounded-lg text-center mb-4">
                  <p className="text-xl font-bold text-blue-800">TVA à l'ENCAISSEMENT</p>
                  <p className="text-sm text-blue-700">(par défaut)</p>
                </div>
                <p className="text-sm mb-4">
                  Tu dois la TVA quand tu es <strong>payé(e)</strong>, y compris sur les acomptes encaissés.
                </p>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-semibold text-amber-800 mb-1">⚡ L'option "TVA sur les débits"</p>
                  <p className="text-xs text-amber-700">
                    Tu peux opter pour rendre la TVA exigible dès la facturation (à la date de la facture) 
                    au lieu d'attendre l'encaissement.
                  </p>
                </div>
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-xs">
                    <strong>Exemple :</strong> Tu factures le 5 février, payé le 20 mars<br/>
                    → TVA due en <strong>mars</strong> (sauf option débits = février)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Biens */}
            <Card className="border-2 border-emerald-200">
              <CardHeader className="bg-emerald-50">
                <CardTitle className="flex items-center gap-2 text-emerald-900">
                  <Truck className="h-5 w-5" />
                  Vente de biens
                </CardTitle>
                <p className="text-sm text-emerald-700">E-commerce, achat-revente, commerce...</p>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="p-4 bg-emerald-100 rounded-lg text-center mb-4">
                  <p className="text-xl font-bold text-emerald-800">TVA à la LIVRAISON</p>
                  <p className="text-sm text-emerald-700">(proche de la date de facture)</p>
                </div>
                <p className="text-sm mb-4">
                  TVA exigible à la livraison. <strong>Exception :</strong> si acompte avant livraison, 
                  TVA exigible à l'encaissement de l'acompte.
                </p>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-semibold text-red-800 mb-1">⚠️ Attention piège !</p>
                  <p className="text-xs text-red-700">
                    Tu peux devoir payer la TVA AVANT d'être payé(e) par ton client !
                  </p>
                </div>
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-xs">
                    <strong>Exemple :</strong> Livraison le 10 février, payé le 15 mars<br/>
                    → TVA due en <strong>février</strong> (à la livraison)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Phrase à retenir */}
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-6 text-center">
              <p className="text-lg font-semibold mb-4">La phrase simple à retenir (version "terrain")</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="font-bold text-blue-700 mb-1">Services</p>
                  <p className="text-sm">"Je paie la TVA quand je suis payé(e)"</p>
                  <p className="text-xs text-muted-foreground">(sauf option débits)</p>
                </div>
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="font-bold text-emerald-700 mb-1">Biens</p>
                  <p className="text-sm">"Je paie la TVA à la livraison... et sur les acomptes quand ils tombent"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PIÈGES TRÉSORERIE */}
        <TabsContent value="pieges" className="space-y-6 mt-6">
          <Card className="border-2 border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-900">
                <AlertTriangle className="h-5 w-5" />
                Le piège de trésorerie à éviter
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="p-4 bg-red-100 rounded-lg mb-4">
                <p className="font-bold text-red-900 text-center">
                  Tu peux devoir payer la TVA avant d'avoir encaissé,<br/>
                  mais ne pas encore avoir de chiffre déclaré à l'URSSAF.
                </p>
              </div>

              <div className="space-y-4">
                <div className="p-4 border-l-4 border-red-500 bg-red-50">
                  <p className="font-semibold text-red-800 mb-2">🚨 Cas critique : Vente de biens</p>
                  <p className="text-sm text-red-700 mb-2">
                    Tu livres en février, le client paie en avril.
                  </p>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>→ TVA à payer en <strong>février</strong> (livraison)</li>
                    <li>→ URSSAF à déclarer en <strong>avril</strong> (encaissement)</li>
                    <li>→ <strong>Résultat :</strong> tu paies la TVA avec l'argent de... personne 😬</li>
                  </ul>
                </div>

                <div className="p-4 border-l-4 border-emerald-500 bg-emerald-50">
                  <p className="font-semibold text-emerald-800 mb-2">✅ Cas safe : Prestations de services</p>
                  <p className="text-sm text-emerald-700 mb-2">
                    Tu factures en février, payé en avril.
                  </p>
                  <ul className="text-sm text-emerald-700 space-y-1">
                    <li>→ TVA à payer en <strong>avril</strong> (encaissement)</li>
                    <li>→ URSSAF à déclarer en <strong>avril</strong> (encaissement)</li>
                    <li>→ <strong>Résultat :</strong> tout est synchronisé, pas de stress 😌</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conseils */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                Comment éviter le piège ?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold mb-2">1. Demander des acomptes</p>
                  <p className="text-sm text-muted-foreground">
                    Si tu vends des biens, demande un acompte avant livraison. 
                    La TVA sera due à l'encaissement de l'acompte = tu as l'argent !
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold mb-2">2. Provisionner la TVA</p>
                  <p className="text-sm text-muted-foreground">
                    Mets de côté 20% de chaque vente dès la livraison, 
                    même si tu n'es pas encore payé(e).
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold mb-2">3. Surveiller les délais de paiement</p>
                  <p className="text-sm text-muted-foreground">
                    Plus tes clients mettent du temps à payer, plus le décalage TVA/tréso est important.
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold mb-2">4. Connaître ses échéances</p>
                  <p className="text-sm text-muted-foreground">
                    Note les dates de déclaration TVA et prépare les montants en avance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReglesPage;
